import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getUserPurchases } from '../services/paymentService';
import { getUserCredits } from '../services/api';
import { subscriptionHasEnded } from '../utils/subscriptionState';

export const PaymentContext = createContext();

// =============================================================================
// TIER DEFINITIONS
// =============================================================================
// =============================================================================
// TIER DEFINITIONS
// =============================================================================
export const TIERS = {
    FREE: 'free',
    MONTHLY: 'monthly',
    SEASONAL: 'season_pass'
};

// Tier feature limits. No prices here: nothing read them, and a typed Season
// Pass price goes false at the season deadline (constants/seasonPricing.js
// is the one clock).
const TIER_FEATURES = {
    [TIERS.FREE]: {
        aiMessagesLimit: 10,           // 10 messages per month
        canAccessLaunchpad: false,     // No launchpad access
        canAnalyzeFit: false,          // No fit analysis
        hasFullFitAnalysis: false,     // N/A
        canDeepResearch: false,        // No deep research
        label: 'Free'
    },
    [TIERS.MONTHLY]: {
        aiMessagesLimit: 30,           // 30 messages per month
        canAccessLaunchpad: true,      // Full launchpad access
        canAnalyzeFit: true,           // Basic fit analysis
        hasFullFitAnalysis: false,     // No recommendations/advanced features
        canDeepResearch: false,        // No deep research
        label: 'Monthly'
    },
    [TIERS.SEASONAL]: {
        aiMessagesLimit: 50,           // 50 messages per month
        canAccessLaunchpad: true,      // Full launchpad access
        canAnalyzeFit: true,           // Full fit analysis
        hasFullFitAnalysis: true,      // Includes recommendations, essay angles, etc.
        canDeepResearch: true,         // Full deep research
        label: 'Season Pass'
    }
};

// Helper to get current month key for localStorage
const getMonthKey = () => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}`;
};

export const usePayment = () => {
    const context = useContext(PaymentContext);
    if (!context) {
        throw new Error('usePayment must be used within a PaymentProvider');
    }
    return context;
};

export const PaymentProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [purchases, setPurchases] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeReason, setUpgradeReason] = useState('');
    const [upgradeFeature, setUpgradeFeature] = useState('');

    // Credits state
    const [credits, setCredits] = useState({
        tier: 'free',
        credits_remaining: 3,
        credits_total: 3,
        credits_used: 0,
        subscription_active: false
    });
    const [creditsLoading, setCreditsLoading] = useState(true);
    const [showCreditsModal, setShowCreditsModal] = useState(false);
    const [creditsModalFeature, setCreditsModalFeature] = useState('fit analysis');
    // The school the sheet was opened for (#5631): named on the one-plan
    // sheet's line; null when the action was not a read of one school.
    const [creditsModalSchool, setCreditsModalSchool] = useState(null);

    // Monthly message tracking with localStorage (for free tier)
    const [monthlyMessagesUsed, setMonthlyMessagesUsed] = useState(() => {
        try {
            const stored = localStorage.getItem('monthlyMessagesUsed');
            if (stored) {
                const data = JSON.parse(stored);
                if (data.month === getMonthKey()) {
                    return data.count;
                }
            }
            return 0;
        } catch {
            return 0;
        }
    });

    // Save monthly messages to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('monthlyMessagesUsed', JSON.stringify({
            month: getMonthKey(),
            count: monthlyMessagesUsed
        }));
    }, [monthlyMessagesUsed]);

    // Fetch purchases and credits on user change
    useEffect(() => {
        if (currentUser?.email) {
            fetchPurchases();
            fetchCredits();
        } else {
            setPurchases(null);
            setCredits({ tier: 'free', credits_remaining: 3, credits_total: 3, credits_used: 0 });
            setLoading(false);
            setCreditsLoading(false);
        }
    }, [currentUser?.email]);

    const fetchPurchases = useCallback(async () => {
        if (!currentUser?.email) return;

        setLoading(true);
        try {
            const result = await getUserPurchases(currentUser.email);
            if (result.success) {
                setPurchases(result.purchases);
            }
        } catch (error) {
            console.error('Error fetching purchases:', error);
        } finally {
            setLoading(false);
        }
    }, [currentUser?.email]);

    const fetchCredits = useCallback(async () => {
        if (!currentUser?.email) return;

        setCreditsLoading(true);
        try {
            const result = await getUserCredits(currentUser.email);
            if (result.success && result.credits) {
                setCredits(result.credits);
                // Returned so PaymentSuccess's post-purchase poll (#1212) can
                // stop as soon as the webhook's grant is visible — React state
                // is async, so the caller can't read `credits` back in time.
                return result.credits;
            }
        } catch (error) {
            console.error('Error fetching credits:', error);
        } finally {
            setCreditsLoading(false);
        }
        return null;
    }, [currentUser?.email]);

    // =============================================================================
    // DETERMINE USER'S TIER
    // =============================================================================
    // A Stripe subscription whose scheduled cancellation has passed is
    // ended, even while the stored tier still says monthly because the
    // webhook that would have cleared it never came (#4619). A comp is a
    // separate grant on the credits doc and is not ended by this.
    const compActive = Boolean(credits?.comp && !credits.comp.expired);
    const planEnded = !compActive && subscriptionHasEnded(purchases);

    const getUserTier = useCallback(() => {
        // Single source of truth: credits collection from Firestore (updated by payment_manager)
        // The backend now syncs both purchases and credits on payment success
        if (planEnded) return TIERS.FREE;

        // Check credits.tier first (authoritative source)
        if (credits?.tier && credits.tier !== 'free') {
            const tier = credits.tier.toLowerCase();
            if (tier === 'season_pass' || tier.includes('season') || tier.includes('annual') || tier.includes('year')) {
                return TIERS.SEASONAL;
            }
            if (tier === 'monthly' || tier.includes('month') || tier.includes('pro')) {
                return TIERS.MONTHLY;
            }
        }

        // Check credits.subscription_active
        if (credits?.subscription_active) {
            const plan = credits?.subscription_plan?.toLowerCase() || credits?.tier?.toLowerCase() || '';
            if (plan.includes('season') || plan.includes('annual') || plan.includes('year')) {
                return TIERS.SEASONAL;
            }
            return TIERS.MONTHLY;
        }

        // Default to FREE tier
        return TIERS.FREE;
    }, [credits, planEnded]);

    const currentTier = getUserTier();
    const tierFeatures = TIER_FEATURES[currentTier];

    // =============================================================================
    // FEATURE ACCESS HELPERS
    // =============================================================================

    // Can access Launchpad?
    const canAccessLaunchpad = tierFeatures.canAccessLaunchpad;

    // Can analyze fit?
    const canAnalyzeFit = tierFeatures.canAnalyzeFit;

    // Has full fit analysis (with recommendations)?
    const hasFullFitAnalysis = tierFeatures.hasFullFitAnalysis;

    // Can do deep research?
    const canDeepResearch = tierFeatures.canDeepResearch;

    // =============================================================================
    // AI MESSAGE TRACKING
    // =============================================================================
    const aiMessagesLimit = tierFeatures.aiMessagesLimit;
    const aiMessagesUsed = currentTier === TIERS.FREE ? monthlyMessagesUsed : 0;
    const aiMessagesAvailable = aiMessagesLimit === 'unlimited'
        ? 'unlimited'
        : Math.max(0, aiMessagesLimit - aiMessagesUsed);

    // =============================================================================
    // UPGRADE PROMPT
    // =============================================================================
    const promptUpgrade = useCallback((feature, reason) => {
        setUpgradeFeature(feature);
        setUpgradeReason(reason);
        setShowUpgradeModal(true);
    }, []);

    const closeUpgradeModal = useCallback(() => {
        setShowUpgradeModal(false);
        setUpgradeReason('');
        setUpgradeFeature('');
    }, []);

    // Consume an AI message
    const consumeAiMessage = useCallback(() => {
        if (aiMessagesAvailable <= 0) {
            promptUpgrade('ai_messages', 'You\'ve used all your monthly messages. Upgrade for more!');
            return false;
        }

        setMonthlyMessagesUsed(prev => prev + 1);
        return true;
    }, [aiMessagesAvailable, promptUpgrade]);

    // =============================================================================
    // CONTEXT VALUE
    // =============================================================================

    // The 7-day reverse trial (#4703): get-credits carries its state and dates
    // only. A trial account holds the Season Pass tier, so every Pass gate
    // opens; `isTrial` is what keeps the paid-only words ("your Pass covers
    // it", "Already subscribed", Cancel) and the no-upsell rule off it.
    const trial = credits?.trial && typeof credits.trial === 'object' ? credits.trial : null;
    // A purchase or comp inside the 7 days ends the trial for every surface
    // (review 2026-10-18: a buyer was offered the Pass again); the server's
    // view says so too, this is the belt.
    const isTrial = trial?.state === 'active' && !credits?.subscription_active && !compActive;
    const trialEnded = trial?.state === 'ended' && trial?.outcome !== 'paid' && currentTier === TIERS.FREE;
    // One free read, then the price (#5631): get-credits carries
    // {rule, spent, first_school} for a FREE account made under the rule
    // only (the server decides from the account's own record; the stamp
    // never leaves it). Null for every other account, so a surface that
    // reads it renders today's words. A Pass holder is never under it.
    const freeRead = (credits?.free_read && typeof credits.free_read === 'object'
        && currentTier === TIERS.FREE) ? credits.free_read : null;

    // Derived credits values
    const creditsRemaining = credits?.credits_remaining ?? 0;
    const creditsTier = credits?.tier ?? 'free';
    const hasCredits = creditsRemaining > 0;
    const canRunFitAnalysis = hasCredits;

    const value = {
        // State
        purchases,
        loading,
        showUpgradeModal,
        upgradeReason,
        upgradeFeature,

        // Current Tier
        currentTier,
        tierLabel: tierFeatures.label,

        // Tier checks
        // Tier checks
        isFreeTier: currentTier === TIERS.FREE,
        isSeasonal: !planEnded && (currentTier === TIERS.SEASONAL || creditsTier === 'season_pass'),
        // Ensure strictly mutually exclusive - Seasonal takes precedence
        isMonthly: !planEnded && (currentTier === TIERS.MONTHLY || creditsTier === 'monthly' || creditsTier === 'pro') && !(currentTier === TIERS.SEASONAL || creditsTier === 'season_pass'),
        // True when the paid plan's cancellation date has passed (#4619).
        subscriptionEnded: planEnded,
        hasActiveSubscription: currentTier !== TIERS.FREE && !isTrial,
        trial,
        isTrial,
        trialEnded,
        freeRead,
        // Admin-comped plan (comp_subscriptions, 2026-07-31): present + unexpired
        // means the tier is a gift, not a Stripe subscription — surfaces show
        // "Complimentary through <date>" instead of Stripe renewal language.
        comp: (credits?.comp && !credits.comp.expired) ? credits.comp : null,

        // Feature access
        canAccessLaunchpad,
        canAnalyzeFit,
        hasFullFitAnalysis,
        canDeepResearch,

        // AI Messages
        aiMessagesLimit,
        aiMessagesUsed,
        aiMessagesAvailable,

        // Credits
        credits,
        creditsRemaining,
        creditsTier,
        creditsLoading,
        hasCredits,
        canRunFitAnalysis,
        fetchCredits,

        // Credits Modal
        showCreditsModal,
        creditsModalFeature,
        creditsModalSchool,
        promptCreditsUpgrade: (feature = 'fit analysis', school = null) => {
            setCreditsModalFeature(feature);
            setCreditsModalSchool(school || null);
            setShowCreditsModal(true);
        },
        closeCreditsModal: () => setShowCreditsModal(false),

        // Actions
        fetchPurchases,
        consumeAiMessage,
        promptUpgrade,
        closeUpgradeModal,

        // Tier definitions (for pricing page)
        TIERS,
        TIER_FEATURES,
    };

    return (
        <PaymentContext.Provider value={value}>
            {children}
        </PaymentContext.Provider>
    );
};

export default PaymentContext;
